import { createColorParam, type ColorParam } from '@paint-ai/shared';
import { ColorSwatch } from '@/components/ui';
import type { Shade } from '../../lib/shade-library';
import { ShadeSearch } from './ShadeSearch';

export interface ColorSlotEditorProps {
  id: string;
  label: string;
  role: 'Primary' | 'Trim' | 'Accent';
  color: ColorParam;
  shades: Shade[];
  onChange: (color: ColorParam) => void;
  searchOpen: boolean;
  onSearchOpen: () => void;
  onSearchClose: () => void;
}

const fieldLabel = 'text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-0.5';
const field =
  'w-full bg-white border border-slate-200 rounded-lg py-1.5 text-xs font-bold focus:outline-none focus:border-blue-500 shadow-2xs';

/** One colour surface (wall, trim or highlight): shade search plus name / code / HEX fields. */
export function ColorSlotEditor({
  id,
  label,
  role,
  color,
  shades,
  onChange,
  searchOpen,
  onSearchOpen,
  onSearchClose,
}: ColorSlotEditorProps) {
  const update = (changes: Partial<Pick<ColorParam, 'name' | 'code' | 'hex'>>) => {
    const next = { name: color.name, code: color.code, hex: color.hex, ...changes };
    onChange(createColorParam(next.name, next.hex, next.code));
  };

  return (
    <div className="p-4 bg-slate-50/90 rounded-xl border border-slate-200/90 space-y-3 hover:border-slate-300 transition-colors relative">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-extrabold text-slate-800 tracking-wide flex items-center gap-1.5">
          <ColorSwatch hex={color.hex} className="w-2.5 h-2.5 border-0 shadow-none" />
          {label}
        </h3>
        <div className="flex items-center gap-2">
          {color.code && (
            <span className="text-[10px] font-mono font-bold text-blue-700 bg-blue-50 border border-blue-200/60 px-2 py-0.5 rounded-md">
              Shade Code: {color.code}
            </span>
          )}
          <span className="text-[10px] font-mono text-slate-500 font-bold bg-slate-200/60 px-2 py-0.5 rounded-md">
            {color.hex}
          </span>
        </div>
      </div>

      <ShadeSearch
        label={label}
        shades={shades}
        open={searchOpen}
        onOpen={onSearchOpen}
        onClose={onSearchClose}
        onPick={(shade) => onChange(createColorParam(shade.name, shade.hex, shade.code))}
      />

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 pt-1">
        <div
          className="w-10 h-10 rounded-lg shadow-inner border-2 border-white flex-shrink-0 relative overflow-hidden ring-1 ring-slate-200 cursor-pointer"
          style={{ backgroundColor: color.hex }}
          title="Click to pick custom color"
        >
          <input
            type="color"
            value={color.hex}
            aria-label={`${label} colour picker`}
            onChange={(e) => update({ hex: e.target.value })}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            id={`color-picker-${id}`}
          />
        </div>

        <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2 min-w-0">
          <label className="sm:col-span-5">
            <span className={fieldLabel}>Shade Name</span>
            <input
              type="text"
              value={color.name}
              onChange={(e) => update({ name: e.target.value })}
              className={`${field} px-2.5 text-slate-800`}
              placeholder="Shade Name"
            />
          </label>
          <label className="sm:col-span-4">
            <span className={fieldLabel}>Shade Code</span>
            <input
              type="text"
              value={color.code || ''}
              onChange={(e) => update({ code: e.target.value })}
              className={`${field} px-2.5 font-mono text-slate-700`}
              placeholder="e.g. NP N 2034 P"
            />
          </label>
          <label className="sm:col-span-3">
            <span className={fieldLabel}>HEX Value</span>
            <input
              type="text"
              value={color.hex}
              onChange={(e) => update({ hex: e.target.value })}
              className={`${field} px-2 font-mono text-slate-700 uppercase text-center`}
              placeholder="#FFFFFF"
            />
          </label>
        </div>
      </div>

      <div className="text-[10px] text-slate-400 font-medium flex items-center justify-between pt-0.5">
        <span>{role} Finish Surface</span>
        <span className="font-mono">RGB: ({color.rgb})</span>
      </div>
    </div>
  );
}
